import { useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, Inline, Input, Stack, Tooltip } from '@etg/wings';
import { getInputMaskConfig, InformationIconButton, MaskedInput } from '@eti/components';
import { useProperty, useSnowplowTracking, useTranslation, useValidation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { constants, date as dateUtils, persistInputData } from '@eti/utils';
import { Field as ReduxFormField } from 'redux-form';
import { TRAVEL_DOCUMENT_EXPIRY_DATE } from '../../utils/constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  DATE_PATTERN
} = constants.inputFieldPatterns;
const {
  MOUSE_FLOW_CLASS_NAMES
} = constants;
const infoIconTriggerStyles = css`
  cursor: pointer;

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
  }
`;
const inputStyles = css`
  flex: 1;
`;
const MaskedExpiryDateInput = _ref => {
  let {
    className,
    dateFormat,
    description,
    id,
    input,
    isReadOnly: _isReadOnly,
    label,
    labelRef,
    meta,
    name,
    shouldMarkInputAsRequired,
    ...props
  } = _ref;
  const {
    t,
    nonDebugT
  } = useTranslation();
  const {
    p
  } = useProperty();
  const hasError = Boolean(meta.invalid && !meta.active && (input.value !== '' || meta.touched));
  const errorId = hasError ? `${name}-error` : undefined;
  const resolvedLabel = shouldMarkInputAsRequired ? nonDebugT('Input.Field.Required', label) : label;
  const descriptionId = `${name}-description`;
  const {
    mask,
    pipe
  } = getInputMaskConfig(dateUtils.getExpiryDateConstraints(new Date(), p('IbeClient.TravelerDetails.PassportExpiryDate.MaxFutureYears')), dateFormat);
  return /*#__PURE__*/_jsxs(Stack, {
    className: className,
    spacing: 4,
    children: [/*#__PURE__*/_jsx(Input.Label, {
      htmlFor: name,
      ref: labelRef,
      children: resolvedLabel
    }), /*#__PURE__*/_jsxs(Inline, {
      alignY: "center",
      noWrap: true,
      spacing: 8,
      children: [/*#__PURE__*/_jsx(MaskedInput, {
        autoCorrect: "off",
        className: inputStyles,
        "data-testid": `traveler-travel-document-expiry-date-${id}-input`,
        descriptionId: descriptionId,
        errorId: errorId,
        hasError: hasError,
        id: name,
        inputMode: "numeric",
        mask: mask,
        name: name,
        pattern: DATE_PATTERN,
        pipe: pipe,
        placeholder: dateFormat.toUpperCase(),
        required: Boolean(shouldMarkInputAsRequired),
        ...input,
        ...props
      }), p('IbeClient.TravelerForm.TravelDocumentExpiryDate.Tooltip.Enabled') && /*#__PURE__*/_jsx(Tooltip, {
        content: t('TravelerForm.TravelDocumentExpiryDate.Tooltip'),
        "data-testid": "document-expiry-date-tooltip",
        title: label,
        trigger: "click",
        children: /*#__PURE__*/_jsx(InformationIconButton, {
          className: infoIconTriggerStyles,
          "data-testid": "document-expiry-date-icon",
          size: 24
        })
      })]
    }), /*#__PURE__*/_jsx(Input.Meta, {
      description: description,
      descriptionId: descriptionId,
      error: meta.error,
      errorId: errorId,
      hasError: hasError
    })]
  });
};
MaskedExpiryDateInput.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.string,
  description: PropTypes.string,
  id: PropTypes.string,
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.string
  }),
  isReadOnly: PropTypes.bool,
  label: PropTypes.string,
  labelRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  meta: PropTypes.shape({
    active: PropTypes.bool,
    // eslint-disable-line react/boolean-prop-naming
    error: PropTypes.string,
    invalid: PropTypes.bool,
    // eslint-disable-line react/boolean-prop-naming
    touched: PropTypes.bool // eslint-disable-line react/boolean-prop-naming
  }),
  name: PropTypes.string,
  shouldMarkInputAsRequired: PropTypes.bool
};
const TravelDocumentExpiryDate = _ref2 => {
  let {
    className,
    dateFormat = 'yyyy-mm-dd',
    form,
    hint,
    id,
    isRequired,
    label,
    labelRef,
    validationRuleName
  } = _ref2;
  const {
    p
  } = useProperty();
  const validate = useValidation(validationRuleName);
  const name = `${TRAVEL_DOCUMENT_EXPIRY_DATE}-${id}`;
  const {
    isSnowplowTrackingEnabled,
    sendTrackEvent
  } = useSnowplowTracking();
  const [travelDocumentExpiryDateHasChanged, setTravelDocumentExpiryDateHasChanged] = useState(false);
  const handleSnowplowOnTravelDocumentExpiryDateChange = () => {
    if (isSnowplowTrackingEnabled && !travelDocumentExpiryDateHasChanged) {
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Input',
        label: 'Passport expiry date',
        value: parseInt(id, 10) + 1
      });
      setTravelDocumentExpiryDateHasChanged(true);
    }
  };
  const handleOnBlur = p('IbeClient.TravelerDetails.PersistFormData.Enabled') ? event => {
    persistInputData(form, `${TRAVEL_DOCUMENT_EXPIRY_DATE}-${id}`)(event.target.value);
  } : undefined;
  return /*#__PURE__*/_jsx(ReduxFormField, {
    className: cx(className, MOUSE_FLOW_CLASS_NAMES),
    component: MaskedExpiryDateInput,
    dateFormat: dateFormat,
    description: hint,
    label: label,
    labelRef: labelRef,
    name: name,
    onBlur: handleOnBlur,
    onChange: handleSnowplowOnTravelDocumentExpiryDateChange,
    props: {
      id,
      name
    },
    shouldMarkInputAsRequired: isRequired,
    validate: validate
  });
};
TravelDocumentExpiryDate.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.string,
  form: PropTypes.string,
  hint: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  labelRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  validationRuleName: PropTypes.string
};
export default TravelDocumentExpiryDate;
//# sourceMappingURL=TravelDocumentExpiryDate.js.map